import { avatarDataUriForName } from '../avatar';
declare global { interface Window { __T_AV_RES?: (id?: string|null) => string | undefined } }
export type BracketData = {
  bracketSize: number;
  playerCount: number;
  numByes: number;
  rounds: Array<{
    round: number;
    matches: Array<{
      matchIndex: number;
      a?: { seed: number; user_id: string | null };
      b?: { seed: number; user_id: string | null };
      isBye?: boolean;
      winnerSeed?: number | null;
      aFrom?: { round: number; matchIndex: number };
      bFrom?: { round: number; matchIndex: number };
      result?: { aScore: number; bScore: number } | null;
    }>;
  }>;
  generatedAt?: string;
};

export type NameResolver = (userId: string | null | undefined) => string;

export type MatchLiveStatus = {
  aScore: number;
  bScore: number;
  round: number;
  ballsLeft: number;
  currentPlayerName: string;
  currentPlayerSide?: 'A' | 'B';
  phase?: 'normal' | 'retribution' | 'sudden_death' | 'over';
  suddenDeathRound?: number;
  isFinal?: boolean;
  noRecord?: boolean;
};

export class BracketView {
  private root!: HTMLDivElement;
  private inner!: HTMLDivElement;
  private scale = 1;
  private pan = { x: 0, y: 0 };
  private lastPan?: { x: number; y: number };
  private pinchDist0 = 0;
  private onMatchClick?: (info: { round: number; matchIndex: number; aUserId?: string | null; bUserId?: string | null; result?: { aScore?: number; bScore?: number } | null; shouldObserve?: boolean }) => void;
  private onPlayerProfile?: (userId: string, userName: string) => void;

  public mount(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'tourney-bracket-viewport';
    this.inner = document.createElement('div');
    this.inner.className = 'tourney-bracket-inner';
    this.root.appendChild(this.inner);
    parent.appendChild(this.root);
    this.attachInteractions();
  }

  public render(
    data: BracketData,
    currentRound: number,
    currentUserId?: string,
    resolveName?: NameResolver,
    statuses?: Record<string, MatchLiveStatus | undefined>,
    resolveAvatar?: (userId: string | null | undefined) => string | undefined,
    resolveTournamentPPR?: (userId: string | null | undefined) => number | undefined
  ) {
    if (!this.inner) return;
    const nameOf: NameResolver = resolveName || ((id) => (!id ? '—' : id));
    const inlineAvatarResolver = resolveAvatar ?? ((window as any).__T_AV_RES as ((id?: string | null) => string | undefined) | undefined);
    const avatarSrcFor = (userId: string | null | undefined, fallbackName: string) => {
      const resolved = inlineAvatarResolver?.(userId);
      const safeName = fallbackName || 'player';
      return resolved || avatarDataUriForName(safeName);
    };
    const attachProfileClick = (el: HTMLElement | null | undefined, userId?: string | null, userName?: string) => {
      if (!el || !userId) return;
      if (!this.onPlayerProfile) return;
      const displayName = userName || userId;
      el.dataset.playerId = userId;
      el.dataset.playerName = displayName;
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        this.onPlayerProfile?.(userId, displayName);
      });
    };
    this.inner.innerHTML = '';

    // Layout constants
    const MATCH_H = 140; // px box height approx (accounts for in-progress matches with live status)
    const GAP = 100;     // px vertical gap (includes bottom margin from CSS, prevents overlap)
    const positions = new Map<string, number>(); // key `${round}:${matchIndex}` -> top

    const totalRounds = data.rounds.length;
    data.rounds.forEach((round, ri) => {
      const col = document.createElement('div');
      col.className = 'tourney-bracket-col' + (round.round === currentRound ? ' current-round' : '');
      col.style.position = 'relative';
      const title = document.createElement('div');
      title.className = 'tourney-bracket-col-title';
      // Round title: Championship (1 match), Semi-Finals (2 matches), else Round N
      const matchesInThisRound = round.matches.length;
      if (matchesInThisRound === 1 && round.round === totalRounds) {
        title.textContent = 'Championship';
      } else if (matchesInThisRound === 2 && round.round === totalRounds - 1) {
        title.textContent = 'Semi-Finals';
      } else {
        title.textContent = `Round ${round.round}`;
      }
      col.appendChild(title);

      const colBody = document.createElement('div');
      colBody.style.position = 'relative';
      col.appendChild(colBody);

      const prevRound = ri > 0 ? data.rounds[ri - 1] : null;

      round.matches.forEach((m, mi) => {
        const key = `${round.round}:${m.matchIndex}`;
        let top = 0;
        if (!prevRound) {
          // First round: stack evenly
          top = (m.matchIndex - 1) * (MATCH_H + GAP);
        } else {
          // Center between source matches
          const aKey = `${m.aFrom?.round}:${m.aFrom?.matchIndex}`;
          const bKey = `${m.bFrom?.round}:${m.bFrom?.matchIndex}`;
          const aTop = positions.get(aKey) ?? 0;
          const bTop = positions.get(bKey) ?? aTop;
          top = Math.min(aTop, bTop) + Math.abs(bTop - aTop) / 2;
        }
        positions.set(key, top);

        const box = document.createElement('div');
        box.className = 'tourney-match';
        box.setAttribute('data-mi', String(m.matchIndex));
        box.setAttribute('data-round', String(round.round));
        if (m.a) box.setAttribute('data-a', String(m.a.user_id||''));
        if (m.b) box.setAttribute('data-b', String(m.b.user_id||''));
        box.addEventListener('click', () => {
          // Determine if user should observe (not a player, match has no result yet)
          const isPlayer = (m.a?.user_id === currentUserId || m.b?.user_id === currentUserId);
          const hasResult = !!m.result;
          const shouldObserve = !isPlayer && !hasResult;

          this.onMatchClick?.({
            round: round.round,
            matchIndex: m.matchIndex,
            aUserId: m.a?.user_id,
            bUserId: m.b?.user_id,
            result: m.result || null,
            shouldObserve
          });
        });
        box.style.position = 'absolute';
        box.style.top = `${top}px`;

        // rows (default)
        const aName = nameOf(m.a?.user_id ?? null);
        const bName = nameOf(m.b?.user_id ?? null);
        const isBye = !!m.isBye || (!m.a?.user_id || !m.b?.user_id);

        const rowA = document.createElement('div');
        rowA.className = 'tourney-match-row';
        {
        if (aName !== '—') { // ""
            const img = document.createElement('img');
            img.src = avatarSrcFor(m.a?.user_id, aName);
            img.alt = aName;
            img.className = 'tourney-match-avatar';
            rowA.appendChild(img);
            attachProfileClick(img, m.a?.user_id, aName);
          }
          
          const span = document.createElement('span');
          const pprA = resolveTournamentPPR?.(m.a?.user_id);
          span.innerHTML = (pprA !== undefined && !Number.isNaN(pprA))
            ? `${aName} <span class="tourney-match-ppr">PPR ${pprA.toFixed(2)}</span>`
            : aName;
          rowA.appendChild(span);
          attachProfileClick(span, m.a?.user_id, aName);
        }
        const rowB = document.createElement('div');
        rowB.className = 'tourney-match-row';
        {
        if (bName !== '—') {
            const img = document.createElement('img');
            img.src = avatarSrcFor(m.b?.user_id, bName);
            img.alt = bName;
            img.className = 'tourney-match-avatar';
            rowB.appendChild(img);
            attachProfileClick(img, m.b?.user_id, bName);
          }
          
          const span = document.createElement('span');
          const pprB = resolveTournamentPPR?.(m.b?.user_id);
          span.innerHTML = (pprB !== undefined && !Number.isNaN(pprB))
            ? `${bName} <span class="tourney-match-ppr">PPR ${pprB.toFixed(2)}</span>`
            : bName;
          rowB.appendChild(span);
          attachProfileClick(span, m.b?.user_id, bName);
        }

        const isMyMatch = currentUserId && (m.a?.user_id === currentUserId || m.b?.user_id === currentUserId);
        const isCompleted = !!m.result || !!statuses?.[`${round.round}:${m.matchIndex}`]?.isFinal;

        if (isMyMatch) {
          if (isCompleted) {
            box.classList.add('my-completed-match'); // Subtle highlight for completed games
          } else {
            box.classList.add('my-match'); // Gold highlight for active/upcoming games
          }
        }
        if (isBye) box.classList.add('bye');

        const st = statuses?.[key];
        // Prefer live status final (which may include sudden death scores) over stored result
        if (st && st.isFinal) {
          const statusEl = document.createElement('div');
          statusEl.className = 'tourney-match-status';
          statusEl.style.position = 'relative';

          const aScore = st.aScore;
          const bScore = st.bScore;
          const aWon = aScore >= bScore;
          const bWon = bScore >= aScore;

          const createLine = (name: string, score: number, isWinner: boolean, userId?: string | null) => {
            const lineDiv = document.createElement('div');
            lineDiv.className = 'tourney-match-player-line';
            if (isWinner && aScore !== bScore) {
              lineDiv.classList.add('winner');
            } else if (!isWinner && aScore !== bScore) {
              lineDiv.classList.add('loser');
            }

            const nameEl = document.createElement('span');
            nameEl.className = 'tourney-match-player-name';
            const img = document.createElement('img');
            img.src = avatarSrcFor(name === aName ? m.a?.user_id : m.b?.user_id, name);
            img.alt = name;
            img.className = 'tourney-match-avatar';
            nameEl.appendChild(img);
            const txt = document.createElement('span');
            txt.textContent = name;
            nameEl.appendChild(txt);
            const ppr = resolveTournamentPPR?.(userId);
            if (ppr !== undefined && !Number.isNaN(ppr)) {
              const pprEl = document.createElement('span');
              pprEl.className = 'tourney-match-status-ppr';
              pprEl.textContent = `PPR ${ppr.toFixed(2)}`;
              nameEl.appendChild(pprEl);
            }
            attachProfileClick(nameEl, userId, name);
            attachProfileClick(img, userId, name);
            attachProfileClick(nameEl, userId, name);
            attachProfileClick(img, userId, name);

            const scoreEl = document.createElement('span');
            scoreEl.className = 'tourney-match-player-score';
            scoreEl.textContent = String(score);

            lineDiv.appendChild(nameEl);
            lineDiv.appendChild(scoreEl);
            return lineDiv;
          };

          statusEl.appendChild(createLine(aName, aScore, aWon, m.a?.user_id));
          statusEl.appendChild(createLine(bName, bScore, bWon, m.b?.user_id));

          const finalLabel = document.createElement('div');
          finalLabel.className = 'tourney-match-final-label';
          // If this match reached sudden death, reflect it in the final label
          finalLabel.textContent = st.phase === 'sudden_death' ? 'Final (SD)' : 'Final';
          statusEl.appendChild(finalLabel);

          box.appendChild(statusEl);
        } else if (m.result) {
          // Show final block with names, scores, and winner highlighted (stored)
          const statusEl = document.createElement('div');
          statusEl.className = 'tourney-match-status';
          statusEl.style.position = 'relative';

          const aScore = m.result.aScore ?? 0;
          const bScore = m.result.bScore ?? 0;
          const aWon = aScore >= bScore;
          const bWon = bScore >= aScore;

          // Create line elements with winner styling
          const createLine = (name: string, score: number, isWinner: boolean, userId?: string | null) => {
            const lineDiv = document.createElement('div');
            lineDiv.className = 'tourney-match-player-line';
            if (isWinner && aScore !== bScore) {
              lineDiv.classList.add('winner');
            } else if (!isWinner && aScore !== bScore) {
              lineDiv.classList.add('loser');
            }

            const nameEl = document.createElement('span');
            nameEl.className = 'tourney-match-player-name';
            {
              const img = document.createElement('img');
              img.src = avatarSrcFor(name === aName ? m.a?.user_id : m.b?.user_id, name);
              img.alt = name;
              img.className = 'tourney-match-avatar';
              nameEl.appendChild(img);
              const txt = document.createElement('span');
              txt.textContent = name;
              nameEl.appendChild(txt);
            }

            const scoreEl = document.createElement('span');
            scoreEl.className = 'tourney-match-player-score';
            scoreEl.textContent = String(score);
            const ppr = resolveTournamentPPR?.(userId);
            if (ppr !== undefined && !Number.isNaN(ppr)) {
              const pprEl = document.createElement('span');
              pprEl.className = 'tourney-match-status-ppr';
              pprEl.textContent = `PPR ${ppr.toFixed(2)}`;
              nameEl.appendChild(pprEl);
            }

            lineDiv.appendChild(nameEl);
            lineDiv.appendChild(scoreEl);
            return lineDiv;
          };

          statusEl.appendChild(createLine(aName, aScore, aWon, m.a?.user_id));
          statusEl.appendChild(createLine(bName, bScore, bWon, m.b?.user_id));

          const finalLabel = document.createElement('div');
          finalLabel.className = 'tourney-match-final-label';
          // Heuristic: stored result being a tie at or above 21 implies sudden death occurred
          finalLabel.textContent = (aScore === bScore && aScore >= 21) ? 'Final (SD)' : 'Final';
          statusEl.appendChild(finalLabel);

          box.appendChild(statusEl);
        } else {
          // In-progress or DB-backed status (if available)
          const st2 = statuses?.[key];
          if (st2) {
            const statusEl = document.createElement('div');
            statusEl.className = 'tourney-match-status';
            if (st2.isFinal) {
              // Show final results with winner highlighted
              const aScore = st2.aScore;
              const bScore = st2.bScore;
              const aWon = aScore >= bScore;
              const bWon = bScore >= aScore;

              const createLine = (name: string, score: number, isWinner: boolean, userId?: string | null) => {
                const lineDiv = document.createElement('div');
                lineDiv.className = 'tourney-match-player-line';
                if (isWinner && aScore !== bScore) {
                  lineDiv.classList.add('winner');
                } else if (!isWinner && aScore !== bScore) {
                  lineDiv.classList.add('loser');
                }

                const nameEl = document.createElement('span');
                nameEl.className = 'tourney-match-player-name';
                {
                  const img = document.createElement('img');
                  img.src = avatarSrcFor(name === aName ? m.a?.user_id : m.b?.user_id, name);
                  img.alt = name;
                  img.className = 'tourney-match-avatar';
                  nameEl.appendChild(img);
                  const txt = document.createElement('span');
                  txt.textContent = name;
                  nameEl.appendChild(txt);
                }

                const scoreEl = document.createElement('span');
                scoreEl.className = 'tourney-match-player-score';
                scoreEl.textContent = String(score);
                const ppr = resolveTournamentPPR?.(userId);
                if (ppr !== undefined && !Number.isNaN(ppr)) {
                  const pprEl = document.createElement('span');
                  pprEl.className = 'tourney-match-status-ppr';
                  pprEl.textContent = `PPR ${ppr.toFixed(2)}`;
                  nameEl.appendChild(pprEl);
                }

                lineDiv.appendChild(nameEl);
                lineDiv.appendChild(scoreEl);
                return lineDiv;
              };

              statusEl.appendChild(createLine(aName, aScore, aWon, m.a?.user_id));
              statusEl.appendChild(createLine(bName, bScore, bWon, m.b?.user_id));

              const finalLabel = document.createElement('div');
              finalLabel.className = 'tourney-match-final-label';
              // If status indicates sudden death final, show marker next to FINAL
              finalLabel.textContent = (st2.phase === 'sudden_death') ? 'FINAL (SD)' : 'FINAL';
              statusEl.appendChild(finalLabel);
            } else if (st2.noRecord) {
              statusEl.textContent = `No record found`;
            } else {
              // In-progress match
              const aCurrent = st2.currentPlayerSide === 'A';
              const bCurrent = st2.currentPlayerSide === 'B';
              const ballsLeftRaw = st2.ballsLeft ?? 0;
              const ballsLeft = Math.max(0, Math.min(ballsLeftRaw, 12));
              const ballDisplay = Array(ballsLeft).fill('⚪').join(' ');
              const phaseLabel = st2.phase === 'retribution'
                ? 'Retribution'
                : (st2.phase === 'sudden_death'
                  ? `Sudden Death R${st2.suddenDeathRound || 1}`
                  : (st2.round ? `Round ${st2.round}` : 'Live'));

              const buildLiveRow = (name: string, userId?: string | null, score?: number, isCurrent?: boolean) => {
                const row = document.createElement('div');
                row.className = 'tourney-schedule-live-player-row';
                if (isCurrent) row.classList.add('current-turn');

                const info = document.createElement('div');
                info.className = 'tourney-schedule-live-player-info';

                const img = document.createElement('img');
                img.className = 'tourney-schedule-live-avatar';
                img.src = avatarSrcFor(userId, name);
                img.alt = `${name}'s avatar`;
                info.appendChild(img);

                const details = document.createElement('div');
                details.className = 'tourney-schedule-live-details';
                const nameEl = document.createElement('span');
                nameEl.className = 'tourney-schedule-live-name';
                nameEl.textContent = name;
                const ppr = resolveTournamentPPR?.(userId);
                const pprEl = document.createElement('span');
                pprEl.className = 'tourney-schedule-player-ppr';
                pprEl.textContent = `PPR ${ppr !== undefined && !Number.isNaN(ppr) ? ppr.toFixed(2) : '--'}`;
                details.appendChild(nameEl);
                details.appendChild(pprEl);
                info.appendChild(details);

                const scoreEl = document.createElement('span');
                scoreEl.className = 'tourney-schedule-live-score';
                scoreEl.textContent = String(score ?? 0);

                row.appendChild(info);
                row.appendChild(scoreEl);
                return row;
              };

              const liveBlock = document.createElement('div');
              liveBlock.className = 'tourney-schedule-live-block';
              liveBlock.appendChild(buildLiveRow(aName, m.a?.user_id, st2.aScore, aCurrent));
              const indicator = document.createElement('div');
              indicator.className = 'tourney-schedule-round-indicator';
              indicator.textContent = phaseLabel || 'Live';
              liveBlock.appendChild(indicator);
              liveBlock.appendChild(buildLiveRow(bName, m.b?.user_id, st2.bScore, bCurrent));

              statusEl.appendChild(liveBlock);
              const meta = document.createElement('div');
              meta.className = 'tourney-schedule-live-meta-line';
              const displayIcons = ballDisplay || '—';
              meta.innerHTML = `<span class="tourney-schedule-ball-display">${displayIcons}</span>`;
              statusEl.appendChild(meta);
            }
            box.appendChild(statusEl);
            // When showing a status block, do not duplicate name rows
            rowA.remove();
            rowB.remove();
          } else {
            // No status: show default name rows
            box.appendChild(rowA);
            box.appendChild(rowB);
          }
        }

        // Append default name rows if not already appended
        if (!box.contains(rowA) && !box.contains(rowB)) {
          // already handled; skip
        }
        colBody.appendChild(box);
      });

      // Set column body height to encompass all matches
      const matchesCount = round.matches.length;
      const colHeight = (matchesCount - 1) * (MATCH_H + GAP) + MATCH_H;
      colBody.style.height = `${colHeight}px`;
      this.inner.appendChild(col);
    });
  }

  public destroy() {
    if (this.root && this.root.parentElement) this.root.parentElement.removeChild(this.root);
  }

  private attachInteractions() {
    // Wheel zoom
    this.root.addEventListener('wheel', (e) => {
      if (!e.ctrlKey) return; // require ctrl+wheel to zoom to keep normal scroll usable
      e.preventDefault();
      const delta = -Math.sign(e.deltaY) * 0.1;
      this.scale = Math.max(0.4, Math.min(2.5, this.scale + delta));
      this.applyTransform();
    }, { passive: false });

    // Drag to pan (mouse)
    let dragging = false;
    let start = { x: 0, y: 0 };
    this.root.addEventListener('mousedown', (e) => {
      dragging = true; start = { x: e.clientX, y: e.clientY }; this.lastPan = { ...this.pan };
    });
    window.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - start.x; const dy = e.clientY - start.y;
      this.pan = { x: (this.lastPan?.x || 0) + dx, y: (this.lastPan?.y || 0) + dy };
      this.applyTransform();
    });
    window.addEventListener('mouseup', () => { dragging = false; });

    // Touch pinch to zoom
    this.root.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        this.pinchDist0 = this.touchDist(e);
        this.lastPan = { ...this.pan };
      }
    }, { passive: true });
    this.root.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const d = this.touchDist(e);
        if (this.pinchDist0 > 0) {
          const factor = d / this.pinchDist0;
          this.scale = Math.max(0.4, Math.min(2.5, (this.scale * factor)));
          this.pinchDist0 = d;
          this.applyTransform();
        }
      }
    }, { passive: false });
  }

  private touchDist(e: TouchEvent) {
    const [a, b] = [e.touches[0], e.touches[1]];
    const dx = a.clientX - b.clientX; const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  private applyTransform() {
    if (!this.inner) return;
    this.inner.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
  }

  public setOnMatchClick(cb: (info: { round: number; matchIndex: number; aUserId?: string | null; bUserId?: string | null; result?: { aScore?: number; bScore?: number } | null; shouldObserve?: boolean }) => void) {
    this.onMatchClick = cb;
  }

  public setOnPlayerProfile(cb: (userId: string, userName: string) => void) {
    this.onPlayerProfile = cb;
  }
}









